import { BehaviorSubject } from 'rxjs';
import { ISelectOption } from '../../../selectOptions';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { ActorTypeFilters } from '../filtersTypes/actorTypeFilter.model';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { TextFilter } from '../filtersTypes/textFilter.model';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import moment from 'moment';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';

export interface IAuditLogSelectedFilters {
  objectType: string[];
  action: string[];
  user?: string[];
  search?: string;
  fromDate?: Date;
  toDate?: Date;
  objectIdentifier?: string;
}

export class AuditLogEventsFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public objectTypeFilters: MultiSelectFilters;
  public actionFilters: MultiSelectFilters;
  public actorTypeFilters: ActorTypeFilters;
  public userFilters: MultiSelectFilters;

  public search: TextFilter;
  public fromDate: DateFilter;
  public toDate: DateFilter;
  public objectIdentifier: TextFilter;

  constructor(objectTypeOptions: ISelectOption[] = [], actionOptions: ISelectOption[] = []) {
    this.objectTypeFilters = new MultiSelectFilters(objectTypeOptions);
    this.actionFilters = new MultiSelectFilters(actionOptions);
    this.actorTypeFilters = new ActorTypeFilters();
    this.userFilters = new MultiSelectFilters([]);
    this.search = new TextFilter();
    this.fromDate = new DateFilter();
    this.toDate = new DateFilter();
    this.objectIdentifier = new TextFilter();

    this.onUpdate$ = new BehaviorSubject<ISelectedFilters>(this.getSelectedFilters());
    this._updateAllOptions();
  }

  public getDynamicFilters(): string[] {
    return [];
  }

  public updateOptions(
    objectTypeOptions: ISelectOption[],
    actionOptions: ISelectOption[],
    userOptions: ISelectOption[]
  ): void {
    this.objectTypeFilters = new MultiSelectFilters(objectTypeOptions);
    this.actionFilters = new MultiSelectFilters(actionOptions);
    this.actorTypeFilters = new ActorTypeFilters();
    this.userFilters = new MultiSelectFilters(userOptions);

    this._updateAllOptions();
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public addAutocompleteOptions(options: ISelectOption[]): void {
    if (!options || options.length === 0) return;
    const current = this.allOptions$.getValue();
    const merged = [...current];
    options.forEach((o) => {
      if (!merged.find((m) => m.id === o.id && m.filterType === o.filterType)) {
        merged.push(o);
      }
    });
    this.allOptions$.next(merged);
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    const filtered = this.allOptions$.getValue().filter((o) => o.categoryName === filter);
    // Deduplicate by id+filterType to avoid duplicate entries in categories (notably User)
    const seen = new Set<string>();
    const unique: ISelectOption[] = [];
    filtered.forEach((o) => {
      const key = `${o.id}::${o.filterType}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(o);
      }
    });
    return new BehaviorSubject<ISelectOption[]>(unique);
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.ObjectIdentifier: {
        const v = this.objectIdentifier.getFilterValue?.() as string | undefined;
        return v ? [v] : [];
      }
      default:
        return [];
    }
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selected: ISelectOption[] = [];

    const normalizeCategory = (option: ISelectOption): ISelectOption => {
      let categoryName = option.categoryName as string;

      switch (categoryName) {
        case FilterTypesEnum.ObjectType:
        case FilterTypesEnum.ResourceType:
          categoryName = 'Object Type';
          break;
        case FilterTypesEnum.Action:
        case FilterTypesEnum.ExecutionStatus:
        case FilterTypesEnum.Exempted:
          categoryName = 'Action';
          break;
        case FilterTypesEnum.ActorType:
          categoryName = 'Actor Type';
          break;
        case FilterTypesEnum.User:
          categoryName = 'User';
          break;
        case FilterTypesEnum.ObjectIdentifier:
          categoryName = 'Object Identifier';
          break;
        default:
          break;
      }
      return { ...option, categoryName };
    };

    selected.push(...this.objectTypeFilters.getSelectedOptions().map(normalizeCategory));
    selected.push(...this.actionFilters.getSelectedOptions().map(normalizeCategory));
    selected.push(...this.actorTypeFilters.getSelectedOptions().map(normalizeCategory));
    selected.push(...this.userFilters.getSelectedOptions().map(normalizeCategory));

    const objId = this.objectIdentifier.getFilterValue?.() as string | undefined;
    if (objId && objId.trim()) {
      selected.push({
        id: `object-identifier:${objId}`,
        value: objId,
        displayName: objId,
        filterType: FilterTypesEnum.ObjectIdentifier,
        categoryName: FilterTypesEnum.ObjectIdentifier,
      });
    }

    return selected;
  }

  public getSelectedFilters(): ISelectedFilters {
    const filters: ISelectedFilters = {
      type: this.objectTypeFilters.getSelectedOptions().map((o) => o.value),
      executionStatus: this.actionFilters
        .getSelectedOptions()
        .filter((o) => o.filterType === FilterTypesEnum.ExecutionStatus)
        .map((o) => o.id),
      exempted: this.actionFilters.getSelectedOptions().some((o) => o.filterType === FilterTypesEnum.Exempted),
      actorType: this.actorTypeFilters.getSelectedOptions().map((o) => o.id),
      user: this.userFilters.getSelectedFiltersIds(),
      search: this.search.getFilterValue?.() as string | undefined,
      fromDate: this.fromDate.getFilterValue?.() as Date | undefined,
      toDate: this.toDate.getFilterValue?.() as Date | undefined,
    } as ISelectedFilters;
    return filters;
  }

  public updateFilterFromUserSelection(filterType: FilterTypesEnum | undefined, value: unknown): void {
    let changed = false;

    const setText = (f: TextFilter, v?: string) => {
      const prev = (f.getFilterValue?.() as string | undefined) || undefined;
      if (prev !== (v || undefined)) {
        f.update(v || undefined);
        return true;
      }
      return false;
    };
    const setDate = (f: DateFilter, v?: Date) => {
      const prev = f.getFilterValue?.() as Date | undefined;
      if (!prev && !v) {
        return false;
      }
      if (!prev || !v) {
        f.update(v || undefined);
        return true;
      }
      if (prev instanceof Date && v instanceof Date && prev.getTime() === v.getTime()) {
        return false;
      }
      f.update(v || undefined);
      return true;
    };

    if (filterType === undefined) {
      if (Array.isArray(value)) {
        if (value.length === 0) {
          changed = this.objectTypeFilters.updateIfNeeded([]) || changed;
          changed = this.actionFilters.updateIfNeeded([]) || changed;
          changed = this.actorTypeFilters.updateIfNeeded([]) || changed;
          changed = this.userFilters.updateIfNeeded([]) || changed;
        } else {
          const asOptions = value as ISelectOption[];
          changed =
            this.objectTypeFilters.updateIfNeeded(
              asOptions.filter((o) => o.filterType === FilterTypesEnum.ResourceType)
            ) || changed;
          const actionSelected = asOptions.filter(
            (o) => o.filterType === FilterTypesEnum.ExecutionStatus || o.filterType === FilterTypesEnum.Exempted
          );
          changed = this.actionFilters.updateIfNeeded(actionSelected) || changed;

          const actorTypeSelected = asOptions.filter((o) => o.filterType === FilterTypesEnum.ActorType);
          changed = this.actorTypeFilters.updateIfNeeded(actorTypeSelected) || changed;

          const userSelected = asOptions.filter((o) => o.filterType === FilterTypesEnum.User);
          changed = this.userFilters.updateIfNeeded(userSelected) || changed;
        }
      }
    } else {
      switch (filterType) {
        case FilterTypesEnum.Search:
          changed = setText(this.search, (value as string) || undefined) || changed;
          break;
        case FilterTypesEnum.FromDate:
          changed = setDate(this.fromDate, (value as Date) || undefined) || changed;
          break;
        case FilterTypesEnum.ToDate:
          changed = setDate(this.toDate, (value as Date) || undefined) || changed;
          break;
        case FilterTypesEnum.ActorType: {
          const asOptions = Array.isArray(value) ? (value as ISelectOption[]) : [];
          changed = this.actorTypeFilters.updateIfNeeded(asOptions) || changed;
          break;
        }
        case FilterTypesEnum.User: {
          const asOptions = Array.isArray(value) ? (value as ISelectOption[]) : [];
          changed = this.userFilters.updateIfNeeded(asOptions) || changed;
          break;
        }
        case FilterTypesEnum.ObjectIdentifier:
          changed = setText(this.objectIdentifier, (value as string) || undefined) || changed;
          break;
        default:
          break;
      }
    }

    if (changed) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }): void {
    if (!keyValueFilters || Object.keys(keyValueFilters).length === 0) {
      this.objectTypeFilters.updateIfNeeded([]);
      this.actionFilters.updateIfNeeded([]);
      this.actorTypeFilters.updateIfNeeded([]);
      this.userFilters.updateIfNeeded([]);
      this.objectIdentifier.update(undefined);
      this.onUpdate$.next(this.getSelectedFilters());
      return;
    }

    let changed = false;

    if ('fromDate' in keyValueFilters) {
      const val = keyValueFilters['fromDate']?.[0];
      const js = val && moment(val, 'x').isValid() ? moment(val, 'x').toDate() : undefined;
      changed = (this.fromDate.update(js), true);
    }
    if ('toDate' in keyValueFilters) {
      const val = keyValueFilters['toDate']?.[0];
      const js = val && moment(val, 'x').isValid() ? moment(val, 'x').toDate() : undefined;
      changed = (this.toDate.update(js), true);
    }
    if ('search' in keyValueFilters) {
      const val = keyValueFilters['search']?.[0];
      changed = (this.search.update(val || undefined), true);
    }
    if ('objectIdentifier' in keyValueFilters) {
      const val = keyValueFilters['objectIdentifier']?.[0];
      changed = (this.objectIdentifier.update(val || undefined), true);
    }

    if (changed) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public updateByOptions(filterType: FilterTypesEnum, options: ISelectOption[]): void {
    this.updateFilterFromUserSelection(filterType, options);
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    if (!options || options.length === 0) return;

    let changed = false;

    const toRemove = new Set(options.map((o) => o.id));
    const keepObjectType = this.objectTypeFilters.getSelectedOptions().filter((o) => !toRemove.has(o.id));
    const keepAction = this.actionFilters.getSelectedOptions().filter((o) => !toRemove.has(o.id));
    const keepActorType = this.actorTypeFilters.getSelectedOptions().filter((o) => !toRemove.has(o.id));
    const keepUser = this.userFilters.getSelectedOptions().filter((o) => !toRemove.has(o.id));
    changed = this.objectTypeFilters.updateIfNeeded(keepObjectType) || changed;
    changed = this.actionFilters.updateIfNeeded(keepAction) || changed;
    changed = this.actorTypeFilters.updateIfNeeded(keepActorType) || changed;
    changed = this.userFilters.updateIfNeeded(keepUser) || changed;

    options.forEach((opt) => {
      switch (opt.filterType) {
        case FilterTypesEnum.ObjectIdentifier:
          changed = (this.objectIdentifier.update(undefined), true);
          break;
        default:
          break;
      }
    });

    if (changed) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _updateAllOptions(): void {
    const merged = [
      ...this.objectTypeFilters.getOptions$().value,
      ...this.actionFilters.getOptions$().value,
      ...this.actorTypeFilters.getOptions$().value,
      ...this.userFilters.getOptions$().value,
    ];
    this.allOptions$.next(merged);
  }
}
